import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, Loader2, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  getTimelineChart,
  createAfkPeriod,
  deleteAfkPeriod,
  getWorkerDetails,
} from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tooltip } from '../ui/tooltip';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../ui/modal';

dayjs.extend(relativeTime);

/** Same idea as server worker_health_monitor (minutes without contact). */
const OFFLINE_AFTER_MS = 3 * 60 * 1000;

function hashHue(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = str.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h) % 360;
}

function appColor(name) {
  const hue = hashHue(name || '');
  return `hsl(${hue}, 52%, 42%)`;
}

function clipInterval(startMs, endMs, rangeStartMs, rangeEndMs) {
  const s = Math.max(startMs, rangeStartMs);
  const e = Math.min(endMs, rangeEndMs);
  if (e <= s) return null;
  return { startMs: s, endMs: e };
}

function formatTickLabel(ms, rangeStartMs, rangeEndMs) {
  const multiDay = !dayjs(rangeStartMs).isSame(dayjs(rangeEndMs), 'day');
  return multiDay ? dayjs(ms).format('MMM D, HH:mm') : dayjs(ms).format('HH:mm');
}

function segmentSecondsInWindow(seg, rangeStartMs, rangeEndMs) {
  const s = dayjs(seg.start_time).valueOf();
  const e = dayjs(seg.end_time).valueOf();
  const clipped = clipInterval(s, e, rangeStartMs, rangeEndMs);
  if (!clipped) return 0;
  return (clipped.endMs - clipped.startMs) / 1000;
}

function formatDurationSeconds(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function mergeActivityIntervals(segments, rangeStartMs, rangeEndMs) {
  const raw = [];
  segments.forEach((seg) => {
    const s = dayjs(seg.start_time).valueOf();
    const e = dayjs(seg.end_time).valueOf();
    const c = clipInterval(s, e, rangeStartMs, rangeEndMs);
    if (c) raw.push({ startMs: c.startMs, endMs: c.endMs });
  });
  if (!raw.length) return [];
  raw.sort((a, b) => a.startMs - b.startMs);
  const merged = [];
  let cur = { ...raw[0] };
  for (let i = 1; i < raw.length; i += 1) {
    const n = raw[i];
    if (n.startMs <= cur.endMs) {
      cur.endMs = Math.max(cur.endMs, n.endMs);
    } else {
      merged.push(cur);
      cur = { ...n };
    }
  }
  merged.push(cur);
  return merged;
}

function workerAppearsOffline(worker) {
  if (!worker) return false;
  if (worker.status === 'offline') return true;
  const ls = worker.last_seen ? dayjs(worker.last_seen).valueOf() : 0;
  if (!ls) return false;
  return Date.now() - ls > OFFLINE_AFTER_MS;
}

function ActivityTimelineChart({ workerId, filterParams }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chart, setChart] = useState(null);
  const [afkOpen, setAfkOpen] = useState(false);
  const [afkStart, setAfkStart] = useState('');
  const [afkEnd, setAfkEnd] = useState('');
  const [afkNote, setAfkNote] = useState('');
  const [afkSaving, setAfkSaving] = useState(false);
  const [workerInfo, setWorkerInfo] = useState(null);
  const [zoom, setZoom] = useState(1); // 1× = fits container; >1× scrolls
  const scrollRef = useRef(null);

  const clampZoom = (z) => Math.min(40, Math.max(1, z));
  const zoomIn = () => setZoom((z) => clampZoom(z * 1.6));
  const zoomOut = () => setZoom((z) => clampZoom(z / 1.6));
  const zoomReset = () => setZoom(1);
  const onWheelZoom = (e) => {
    if (!e.ctrlKey) return; // Ctrl+wheel zooms; plain wheel scrolls
    e.preventDefault();
    setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.2 : 1 / 1.2)));
  };

  const load = useCallback(async () => {
    if (!workerId) return;
    setLoading(true);
    setError(null);
    try {
      const [res, worker] = await Promise.all([
        getTimelineChart({
          hours: filterParams.hours,
          workerId,
          startDate: filterParams.startDate,
          endDate: filterParams.endDate,
        }),
        getWorkerDetails(workerId).catch(() => null),
      ]);
      setChart(res.data.data);
      setWorkerInfo(worker && worker.id != null ? worker : null);
    } catch (e) {
      console.error(e);
      setError('Failed to load timeline chart');
      setChart(null);
      setWorkerInfo(null);
    } finally {
      setLoading(false);
    }
  }, [workerId, filterParams.hours, filterParams.startDate, filterParams.endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const range = useMemo(() => {
    if (!chart?.range_start || !chart?.range_end) return null;
    const startMs = dayjs(chart.range_start).valueOf();
    const endMs = dayjs(chart.range_end).valueOf();
    const span = Math.max(endMs - startMs, 60000);
    return { startMs, endMs, span };
  }, [chart]);

  const pct = useCallback(
    (tMs) => {
      if (!range) return 0;
      return ((tMs - range.startMs) / range.span) * 100;
    },
    [range]
  );

  const openAfkDialog = () => {
    if (range) {
      const mid = range.startMs + range.span / 2;
      setAfkStart(dayjs(mid - 15 * 60 * 1000).format('YYYY-MM-DDTHH:mm'));
      setAfkEnd(dayjs(mid + 15 * 60 * 1000).format('YYYY-MM-DDTHH:mm'));
    } else {
      setAfkStart(dayjs().subtract(30, 'minute').format('YYYY-MM-DDTHH:mm'));
      setAfkEnd(dayjs().format('YYYY-MM-DDTHH:mm'));
    }
    setAfkNote('');
    setAfkOpen(true);
  };

  const submitAfk = async () => {
    if (!workerId) return;
    const s = dayjs(afkStart);
    const e = dayjs(afkEnd);
    if (!s.isValid() || !e.isValid() || !e.isAfter(s)) return;
    setAfkSaving(true);
    try {
      await createAfkPeriod({
        worker_id: workerId,
        start_time: s.toISOString(),
        end_time: e.toISOString(),
        note: afkNote.trim() || undefined,
      });
      setAfkOpen(false);
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setAfkSaving(false);
    }
  };

  const removeAfk = async (id) => {
    try {
      await deleteAfkPeriod(id);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const tickCount = 5;
  const ticks = useMemo(() => {
    if (!range) return [];
    const out = [];
    for (let i = 0; i <= tickCount; i += 1) {
      out.push(range.startMs + (range.span * i) / tickCount);
    }
    return out;
  }, [range]);

  const appShares = useMemo(() => {
    const segs = chart?.segments || [];
    if (!chart?.range_start || !chart?.range_end) {
      return { rows: [], total: 0 };
    }
    const startMs = dayjs(chart.range_start).valueOf();
    const endMs = dayjs(chart.range_end).valueOf();
    const byApp = new Map();
    segs.forEach((seg) => {
      const name = seg.application || 'Unknown';
      const add = segmentSecondsInWindow(seg, startMs, endMs);
      if (add <= 0) return;
      byApp.set(name, (byApp.get(name) || 0) + add);
    });
    const rows = [...byApp.entries()]
      .map(([application, seconds]) => ({ application, seconds }))
      .sort((a, b) => b.seconds - a.seconds);
    const total = rows.reduce((s, r) => s + r.seconds, 0);
    return { rows, total };
  }, [chart]);

  const mergedActivity = useMemo(() => {
    if (!chart?.range_start || !chart?.range_end) return [];
    const rs = dayjs(chart.range_start).valueOf();
    const re = dayjs(chart.range_end).valueOf();
    return mergeActivityIntervals(chart?.segments || [], rs, re);
  }, [chart]);

  // ManicTime-style single "Applications" row: every segment is laid out on
  // one shared time axis, coloured by application.
  const appTimelineSegments = useMemo(() => {
    if (!chart?.range_start || !chart?.range_end) return [];
    const rs = dayjs(chart.range_start).valueOf();
    const re = dayjs(chart.range_end).valueOf();
    const out = [];
    (chart?.segments || []).forEach((seg) => {
      const s = dayjs(seg.start_time).valueOf();
      const e = dayjs(seg.end_time).valueOf();
      const c = clipInterval(s, e, rs, re);
      if (!c) return;
      out.push({
        startMs: c.startMs,
        endMs: c.endMs,
        application: seg.application || 'Unknown',
        title: seg.window_title || '',
        risk: seg.risk_score || 0,
      });
    });
    return out.sort((a, b) => a.startMs - b.startMs);
  }, [chart]);

  if (!workerId) {
    return (
      <Alert variant="info">
        <AlertDescription>
          Open Applications from a worker to see the activity timeline.
        </AlertDescription>
      </Alert>
    );
  }

  if (loading && !chart) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !chart) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!range) {
    return null;
  }

  const segments = chart?.segments || [];
  const afkPeriods = chart?.afk_periods || [];
  const spanHours = (range.span / 3600000).toFixed(1).replace(/\.0$/, '');
  const multiDay = !dayjs(range.startMs).isSame(dayjs(range.endMs), 'day');
  const isOffline = workerAppearsOffline(workerInfo);
  const lastSeenMs = workerInfo?.last_seen ? dayjs(workerInfo.last_seen).valueOf() : null;

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        {isOffline && workerInfo ? (
          <Alert variant="warning" className="mb-4">
            <AlertDescription>
              Workstation is <strong>offline</strong> (last seen{' '}
              {dayjs(workerInfo.last_seen).fromNow()} —{' '}
              {dayjs(workerInfo.last_seen).format('MMM D, YYYY HH:mm')}). Gray = no app data;
              green = reported foreground time; dark overlay = after last server contact.
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="max-w-full">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-foreground">Activity over time</h3>
              {workerInfo ? (
                <Badge variant={isOffline ? 'warning' : 'success'}>
                  {isOffline ? 'Offline' : 'Online'}
                </Badge>
              ) : null}
            </div>
            <p className="mb-1 text-sm text-muted-foreground">
              Time runs <strong>left → right</strong>. Window:{' '}
              <strong>
                {dayjs(chart.range_start).format('MMM D, YYYY HH:mm')} —{' '}
                {dayjs(chart.range_end).format('MMM D, YYYY HH:mm')}
              </strong>{' '}
              ({spanHours} h).
              {!filterParams.startDate && !filterParams.endDate && !filterParams.hours
                ? ' With filter “All time”, the chart uses the last 24 hours.'
                : null}
            </p>
            <p className="mb-2 text-sm text-muted-foreground">
              Top bar: <strong>gray</strong> = no segments; <strong>green</strong> = time with
              reported app focus; <strong>dark overlay</strong> = after last server contact when
              offline; <strong>red</strong> = manual AFK. Bottom:{' '}
              <strong>share of foreground time</strong> (hover for %). Segments: {segments.length}.
            </p>
            <div className="mb-2 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="h-3.5 w-3.5 rounded-sm bg-muted" />
                <span className="text-xs text-muted-foreground">No app data</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3.5 w-3.5 rounded-sm bg-success" />
                <span className="text-xs text-muted-foreground">
                  Foreground (from segments)
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="h-3.5 w-3.5 rounded-sm"
                  style={{
                    background: 'linear-gradient(90deg, rgba(0,0,0,0.5), rgba(0,0,0,0.2))',
                  }}
                />
                <span className="text-xs text-muted-foreground">No contact (offline)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3.5 w-3.5 rounded-sm bg-destructive" />
                <span className="text-xs text-muted-foreground">AFK (manual)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="h-3.5 w-3.5 rounded-sm"
                  style={{ background: 'linear-gradient(90deg, #888 0%, #444 100%)' }}
                />
                <span className="text-xs text-muted-foreground">
                  Apps (width = % of tracked time)
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <Button variant="outline" size="icon-sm" onClick={zoomOut} title="Zoom out" disabled={zoom <= 1}>
              <ZoomOut />
            </Button>
            <span className="min-w-[3.2rem] text-center text-xs tabular-nums text-muted-foreground">
              {zoom.toFixed(1)}×
            </span>
            <Button variant="outline" size="icon-sm" onClick={zoomIn} title="Zoom in (Ctrl+wheel)">
              <ZoomIn />
            </Button>
            <Button variant="outline" size="icon-sm" onClick={zoomReset} title="Reset zoom" disabled={zoom === 1}>
              <Maximize2 />
            </Button>
            <Button variant="outline" size="sm" onClick={openAfkDialog}>
              Mark AFK period
            </Button>
          </div>
        </div>

        {/* Scrollable, zoomable timeline strips */}
        <div
          ref={scrollRef}
          className="overflow-x-auto overflow-y-hidden"
          onWheel={onWheelZoom}
        >
        <div style={{ width: `${zoom * 100}%`, minWidth: '100%' }}>

        {/* Tick labels */}
        <div className="relative mb-0.5 min-h-[28px]">
          <div className="flex items-start justify-between gap-1 px-0.5">
            {ticks.map((t) => (
              <span
                key={t}
                className={cnClass(
                  'flex-1 whitespace-pre-line text-center leading-tight text-muted-foreground',
                  multiDay ? 'text-[0.7rem]' : 'text-xs'
                )}
              >
                {formatTickLabel(t, range.startMs, range.endMs)}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-1 block text-xs font-semibold text-muted-foreground">
          Presence, contact & AFK
        </div>
        <div className="relative mb-4 h-8 overflow-hidden rounded border border-border">
          <div className="absolute inset-0 z-[1] bg-muted" />
          {mergedActivity.map((iv, idx) => {
            const left = pct(iv.startMs);
            const width = pct(iv.endMs) - left;
            return (
              <Tooltip
                key={`act-${iv.startMs}-${idx}`}
                label={`Foreground activity · ${dayjs(iv.startMs).format(
                  'MMM D, HH:mm'
                )} – ${dayjs(iv.endMs).format('MMM D, HH:mm')}`}
              >
                <div
                  className="absolute top-0 bottom-0 z-[2] bg-success opacity-90"
                  style={{
                    left: `${left}%`,
                    width: `${Math.max(width, 0.35)}%`,
                  }}
                />
              </Tooltip>
            );
          })}
          {isOffline && lastSeenMs != null && lastSeenMs < range.endMs
            ? (() => {
                const oStart = Math.max(range.startMs, lastSeenMs);
                const oEnd = range.endMs;
                if (oStart >= oEnd) return null;
                const left = pct(oStart);
                const width = pct(oEnd) - left;
                return (
                  <Tooltip
                    key="offline-overlay"
                    label={`No server contact since ${dayjs(lastSeenMs).format(
                      'MMM D, YYYY HH:mm:ss'
                    )} (${dayjs(lastSeenMs).fromNow()})`}
                  >
                    <div
                      className="absolute top-0 bottom-0 z-[3]"
                      style={{
                        left: `${left}%`,
                        width: `${Math.max(width, 0.35)}%`,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                      }}
                    />
                  </Tooltip>
                );
              })()
            : null}
          <div className="pointer-events-none absolute inset-0 z-[4]">
            {afkPeriods.map((a) => {
              const s = dayjs(a.start_time).valueOf();
              const e = dayjs(a.end_time).valueOf();
              const clipped = clipInterval(s, e, range.startMs, range.endMs);
              if (!clipped) return null;
              const left = pct(clipped.startMs);
              const width = pct(clipped.endMs) - left;
              return (
                <Tooltip
                  key={a.id}
                  label={`AFK${a.note ? `: ${a.note}` : ''} · ${dayjs(a.start_time).format(
                    'MMM D, HH:mm'
                  )} – ${dayjs(a.end_time).format('MMM D, HH:mm')}`}
                >
                  <div
                    className="pointer-events-auto absolute top-0 bottom-0 bg-destructive"
                    style={{
                      left: `${left}%`,
                      width: `${Math.max(width, 0.5)}%`,
                    }}
                  />
                </Tooltip>
              );
            })}
          </div>
          {/* Grid lines */}
          {ticks.map((t) => (
            <div
              key={`grid-${t}`}
              className="pointer-events-none absolute top-0 bottom-0 z-[5] w-px bg-border/60"
              style={{ left: `${pct(t)}%`, transform: 'translateX(-50%)' }}
            />
          ))}
        </div>

        {/* ManicTime-style single Applications row */}
        <div className="mb-1 mt-5 block text-xs font-semibold text-muted-foreground">
          Applications
        </div>
        <div className="relative h-10 overflow-hidden rounded border border-border bg-muted/20">
          {appTimelineSegments.length === 0 ? (
            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
              No application segments in this time window
            </div>
          ) : (
            appTimelineSegments.map((seg, idx) => {
              const left = pct(seg.startMs);
              const width = pct(seg.endMs) - left;
              const durSec = (seg.endMs - seg.startMs) / 1000;
              const color = appColor(seg.application);
              const label = `${seg.application}${seg.title ? ' — ' + seg.title : ''}\n${dayjs(
                seg.startMs
              ).format('MMM D, HH:mm:ss')} – ${dayjs(seg.endMs).format(
                'HH:mm:ss'
              )} · ${formatDurationSeconds(durSec)}${
                seg.risk > 0 ? `\nRisk: ${seg.risk}` : ''
              }`;
              return (
                <Tooltip key={`apptl-${idx}`} label={label} side="top">
                  <div
                    className="absolute top-0 bottom-0 cursor-default transition-[filter] duration-150 hover:brightness-125"
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      backgroundColor: color,
                      outline:
                        seg.risk >= 50 ? '1px solid hsl(var(--destructive))' : undefined,
                    }}
                  />
                </Tooltip>
              );
            })
          )}
          {/* Vertical grid lines aligned with ticks */}
          {ticks.map((t) => (
            <div
              key={`apptl-grid-${t}`}
              className="pointer-events-none absolute top-0 bottom-0 w-px bg-border/60"
              style={{ left: `${pct(t)}%`, transform: 'translateX(-0.5px)' }}
            />
          ))}
        </div>

        <div className="mb-1 mt-5 block text-xs font-semibold text-muted-foreground">
          Applications — share of time (full width)
        </div>
        <div className="flex min-h-[44px] w-full flex-row items-stretch overflow-hidden rounded border border-border bg-muted/40">
          {appShares.total <= 0 ? (
            <div className="flex flex-1 items-center justify-center px-4 py-3">
              <span className="text-sm text-muted-foreground">
                No application segments in this time window
              </span>
            </div>
          ) : (
            appShares.rows.map(({ application, seconds }) => {
              const sharePct = (100 * seconds) / appShares.total;
              const label = `${application}\n${formatDurationSeconds(
                seconds
              )} · ${sharePct.toFixed(1)}%`;
              return (
                <Tooltip key={application} label={label} side="top">
                  <div
                    className="box-border min-h-[44px] cursor-default border-r border-black/10 transition-[filter] duration-150 last:border-r-0 hover:brightness-110"
                    style={{
                      flexGrow: seconds,
                      flexBasis: 0,
                      flexShrink: 1,
                      minWidth: 0,
                      backgroundColor: appColor(application),
                    }}
                  />
                </Tooltip>
              );
            })
          )}
        </div>

        {/* Bottom time-axis tick labels (mirrors top axis, scrolls with zoom) */}
        <div className="relative mt-1 min-h-[24px]">
          <div className="flex items-start justify-between gap-1 px-0.5">
            {ticks.map((t) => (
              <span
                key={`b-${t}`}
                className={cnClass(
                  'flex-1 whitespace-pre-line text-center leading-tight text-muted-foreground',
                  multiDay ? 'text-[0.7rem]' : 'text-xs'
                )}
              >
                {formatTickLabel(t, range.startMs, range.endMs)}
              </span>
            ))}
          </div>
        </div>

        </div>
        </div>

        {afkPeriods.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 text-sm font-semibold text-foreground">
              AFK periods (manual)
            </div>
            <ul className="flex flex-col divide-y divide-border rounded border border-border">
              {afkPeriods.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground">
                      {dayjs(a.start_time).format('MMM D, HH:mm')} —{' '}
                      {dayjs(a.end_time).format('MMM D, HH:mm')}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {a.note || 'AFK'}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeAfk(a.id)}
                    aria-label="delete AFK"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Modal open={afkOpen} onClose={() => !afkSaving && setAfkOpen(false)}>
          <ModalHeader>
            <ModalTitle>Mark AFK time</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Start</label>
              <Input
                type="datetime-local"
                value={afkStart}
                onChange={(ev) => setAfkStart(ev.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">End</label>
              <Input
                type="datetime-local"
                value={afkEnd}
                onChange={(ev) => setAfkEnd(ev.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Note (optional)</label>
              <Input
                value={afkNote}
                onChange={(ev) => setAfkNote(ev.target.value)}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setAfkOpen(false)} disabled={afkSaving}>
              Cancel
            </Button>
            <Button onClick={submitAfk} disabled={afkSaving}>
              Save
            </Button>
          </ModalFooter>
        </Modal>
      </CardContent>
    </Card>
  );
}

// local tiny classnames helper to avoid importing utils in a nested path concern
function cnClass(...parts) {
  return parts.filter(Boolean).join(' ');
}

export default ActivityTimelineChart;
